<template>
    <div class="ticket-header">
        <div class="d-flex justify-space-between">
            <div>
                <div class="d-flex align-center ga-1">
                    <svg-icon :icon="ticket" alt="ticket" />
                    <span class="text-size-lg font-weight-medium text-secondary-dark">{{ name }}</span>
                </div>
            </div>
            <div class="d-flex align-end">
                <div v-if="expired">
                    <div class="text-size-lg text-secondary-dark font-weight-bold">
                        {{ t('travelCard.expired', 'Expired') }}
                    </div>
                </div>
                <div v-else-if="validityTime && isStarted">
                    <div class="text-size-lg text-secondary-dark font-weight-bold">
                        {{ remainingTime }}
                    </div>
                </div>
                <span v-else class="text-size-lg text-secondary-dark font-weight-bold">{{ getStatus }}</span>
            </div>
        </div>
        <div class="d-flex justify-space-between">
            <div class="d-block text-size-xs text-secondary-light">
                <span v-if="geography">{{ getGeography + (categoryName ? ', ' : '') }}</span>
                {{ firstCharToLower(categoryName) }}
            </div>
            <v-spacer />
            <div v-if="validityTime && isStarted" class="text-size-xs text-medium-emphasis">
                {{ expired ? t('travelCard.expired', 'Expired') : t('travelCard.expires', 'Expires') }}:
                {{ formatTimestamp(validityTime) }}
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ticket from '@/assets/ticket.svg';
import SvgIcon from '@/features/common/components/SvgIcon.vue';
import { getGeographyText, isWithinDeliveryTime } from '../../../helpers';
import { firstCharToLower, formatTimestamp } from '@/helpers';
import { ITicketStatusHeader } from '../../../types';
import { differenceInHours } from 'date-fns';

const props = withDefaults(defineProps<ITicketStatusHeader>(), { now: () => new Date() });
const { t } = useI18n();

const getGeography = computed(() => (props.geography ? getGeographyText(props.geography, t('travelCard.allZones', 'All zones')) : ''));
const getStatus = computed(() =>
    isWithinDeliveryTime(props.orderTime, props.now?.getTime())
        ? t('travelCard.productOnTheWay', 'On the way to the card')
        : t('travelCard.productNotActivated', 'Not activated')
);
const remainingTime = computed(() => {
    if (!props.validityTime || !props.now) return 0;
    const validityDate = new Date(props.validityTime * 1000);

    // calculate remaining time in hours
    const diff = differenceInHours(validityDate, props.now);

    // if more than 24, return remaining time in days
    if (diff > 24) {
        return t(
            'travelCard.timeLeft',
            { time: t('units.days', { count: Math.ceil(diff / 24) }, '0 days | 1 day | {count} days') },
            '{time} left'
        );
    }

    // else return remaining time in hours
    return t('travelCard.timeLeft', { time: t('units.hours', { count: diff }, '0 hours | 1 hour | {count} hours') }, '{time} left');
});
const expired = computed(() => {
    if (!props.validityTime || !props.now) return false;
    return props.validityTime * 1000 < props.now.getTime();
});
</script>
